<script>
import appConfig from '@/app.config'
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import { http } from '@/helpers/easyindustriaapi/config'

export default {
  page: {
    title: 'Resumo Repasse por Profissional',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  name: 'ResumoRepasseProfissional',
  components: { Layout, PageHeader },
  data() {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    return {
      titleBody: 'Resumo Repasse por Profissional',
      items: [
        { text: 'Relatórios', href: '/relatorios/ficha' },
        { text: 'Resumo Repasse por Profissional', active: true }
      ],
      currentEmpresa: null,
      listaProfissionais: [],
      loadingProfessionals: false,
      isLoading: false,
      isLoadingPdf: false,
      error: null,
      report: null,
      hideProfessionalSelect: false,
      filters: {
        profissional_id: null,
        data_inicial: this.formatDateForInput(firstDay),
        data_final: this.formatDateForInput(lastDay)
      },
      fields: [
        { key: 'paciente', label: 'Paciente', sortable: true, tdClass: 'text-left', thClass: 'text-center' },
        { key: 'quantidade', label: 'Quantidade', sortable: true, tdClass: 'text-right', thClass: 'text-center' },
        { key: 'valor', label: 'Valor (R$)', sortable: true, tdClass: 'text-right', thClass: 'text-center' }
      ]
    }
  },
  created() {
    this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa'))
    const q = this.$route?.query || {}
    if (q.from === 'calendar' && q.profissional_id) {
      this.filters.profissional_id = parseInt(q.profissional_id, 10)
      this.hideProfessionalSelect = true
    }
  },
  mounted() {
    if (!this.hideProfessionalSelect) {
      this.loadProfessionals()
    }
  },
  computed: {
    professionalOptions() {
      return this.listaProfissionais
        .filter(p => p.associated_user_id)
        .map(p => ({
          value: p.id,
          text: p.name
        }))
    },
    canGenerate() {
      return Boolean(this.filters.profissional_id)
    },
    showProfessionalSelect() {
      return !this.hideProfessionalSelect
    }
  },
  methods: {
    formatDateForInput(date) {
      const d = new Date(date)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    async loadProfessionals() {
      if (!this.currentEmpresa || !this.currentEmpresa.id) return
      this.loadingProfessionals = true
      try {
        const res = await http.get(`/professional?empresa_id=${this.currentEmpresa.id}`)
        this.listaProfissionais = Array.isArray(res.data) ? res.data : (res.data?.professionals || res.data || [])
      } catch (e) {
        this.error = e.response?.data?.message || 'Erro ao carregar profissionais.'
      } finally {
        this.loadingProfessionals = false
      }
    },
    async generateReport() {
      if (!this.canGenerate) return
      this.error = null
      this.report = null
      this.isLoading = true
      try {
        const params = {
          profissional_id: this.filters.profissional_id,
          data_inicial: this.filters.data_inicial,
          data_final: this.filters.data_final
        }
        const res = await http.get('/relatorios/resumo-repasse-profissional', { params })
        this.report = res.data
      } catch (e) {
        const data = e.response?.data
        const msg = data?.message || (data?.errors ? Object.values(data.errors).flat().join(' ') : null) || 'Erro ao gerar relatório.'
        this.error = msg
      } finally {
        this.isLoading = false
      }
    },
    formatMoney(value) {
      if (value == null) return '0,00'
      return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    async openPdf() {
      if (!this.canGenerate) return
      this.isLoadingPdf = true
      this.error = null
      try {
        const params = {
          profissional_id: this.filters.profissional_id,
          data_inicial: this.filters.data_inicial,
          data_final: this.filters.data_final
        }
        const res = await http.get('/relatorios/resumo-repasse-profissional/pdf', {
          params,
          responseType: 'blob'
        })
        const blob = new Blob([res.data], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank', 'noopener,noreferrer')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (e) {
        const data = e.response?.data
        let msg = 'Erro ao gerar PDF. Verifique os filtros e permissões.'
        if (data && typeof data === 'object' && !(data instanceof Blob)) {
          if (data.message) msg = data.message
          else if (data.errors) msg = Object.values(data.errors).flat().join(' ')
        } else if (typeof data === 'string') msg = data
        this.error = msg
      } finally {
        this.isLoadingPdf = false
      }
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="titleBody" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Filtros</h5>
            <div class="row">
              <div v-if="showProfessionalSelect" class="col-md-4">
                <b-form-group label="Profissional" label-for="profissional">
                  <b-form-select
                    id="profissional"
                    v-model="filters.profissional_id"
                    :options="professionalOptions"
                    :disabled="loadingProfessionals"
                  >
                    <template #first>
                      <b-form-select-option :value="null" disabled>-- Selecione --</b-form-select-option>
                    </template>
                  </b-form-select>
                  <small v-if="loadingProfessionals" class="text-muted">Carregando...</small>
                </b-form-group>
              </div>
              <div :class="showProfessionalSelect ? 'col-md-3' : 'col-md-4'">
                <b-form-group label="Data inicial" label-for="data_inicial">
                  <b-form-input
                    id="data_inicial"
                    v-model="filters.data_inicial"
                    type="date"
                  />
                </b-form-group>
              </div>
              <div :class="showProfessionalSelect ? 'col-md-3' : 'col-md-4'">
                <b-form-group label="Data final" label-for="data_final">
                  <b-form-input
                    id="data_final"
                    v-model="filters.data_final"
                    type="date"
                  />
                </b-form-group>
              </div>
              <div class="col-md-2 d-flex align-items-end pb-3">
                <b-button
                  variant="primary"
                  :disabled="!canGenerate || isLoading"
                  @click="generateReport"
                >
                  <b-spinner v-if="isLoading" small class="mr-1" />
                  {{ isLoading ? 'Gerando...' : 'Gerar Relatório' }}
                </b-button>
                <b-button
                  variant="outline-primary"
                  class="ml-2"
                  :disabled="!canGenerate || isLoadingPdf"
                  @click="openPdf"
                >
                  <b-spinner v-if="isLoadingPdf" small class="mr-1" />
                  {{ isLoadingPdf ? 'Abrindo...' : 'Abrir PDF' }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-alert v-if="error" variant="danger" show dismissible @dismissed="error = null">
      {{ error }}
    </b-alert>

    <div v-if="report" class="row mt-3">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">
              Período: {{ report.periodo }} — Profissional: {{ report.profissional }}
            </h5>
            <b-table
              :items="report.itens || []"
              :fields="fields"
              striped
              responsive="sm"
              show-empty
              empty-text="Nenhum atendimento no período."
              foot-clone
            >
              <template #cell(valor)="data">
                {{ formatMoney(data.value) }}
              </template>
              <template #foot(valor)="">
                <strong>{{ formatMoney(report.total_geral) }}</strong>
              </template>
              <template #foot(quantidade)="">
                <strong>{{ (report.itens || []).reduce((s, i) => s + (i.quantidade || 0), 0) }}</strong>
              </template>
              <template #foot(paciente)="">
                <strong>Total geral</strong>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!report && !isLoading && !error" class="row mt-3">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5 text-muted">
            Selecione um profissional e clique em "Gerar Relatório" para visualizar o resumo de repasses.
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.table th {
  font-weight: 600;
  vertical-align: middle;
}
.table td {
  vertical-align: middle;
}
</style>
